import { createRoot } from 'react-dom/client';

/**
 * The one way a search widget is put on a page (item #8773).
 *
 * It lives in its own module rather than in either bootstrap because both of
 * them need it and neither may import the other: standalone.jsx initializes the
 * customer embed on import, so pulling it into the demo bundle would run the
 * customer bootstrap on a demo page.
 *
 * The shadow root is otherwise unstyled, per the standalone-bundle convention:
 * the widget's components use inline styles plus host inheritance, and any
 * responsive rules ship as a scoped <style> from the component itself.
 *
 * @param {Element} hostEl - element to attach the shadow root to
 * @param {Object} [options]
 * @param {'page'|'self'} [options.overlayScope='page'] - where this widget's
 *   overlay is laid out
 * @param {function({portalTarget: Element|null}): React.ReactNode} options.render -
 *   the component tree to mount, given the portal target a scoped overlay needs
 * @returns {{shadow: ShadowRoot, mountPoint: Element, root: import('react-dom/client').Root}}
 */
export function mountWidgetInShadowHost(hostEl, { overlayScope = 'page', render } = {}) {
  // A host that already has a root is one being re-mounted: the landing's tabs
  // unmount a demo when the visitor switches away and mount it again on the way
  // back (item #8854), and attachShadow throws on a second call.
  const shadow = hostEl.shadowRoot || hostEl.attachShadow({ mode: 'open' });
  const mountPoint = shadow.firstElementChild || document.createElement('div');
  // A scoped overlay portals into this root and lays its box out against the
  // nearest positioned ancestor (item #8711). A shadow root is not a containing
  // block, so without one the overlay's `position: absolute` climbs past the
  // placement to the viewport and covers the page from inside the panel it was
  // meant to stay in. Page-scoped placements have nothing portalling into them
  // and stay unpositioned.
  //
  // The HOST, not the mount point. A host element positions the whole slot it
  // gave the widget; the mount point is a box inside it whose size is whatever
  // the widget renders. Positioning the mount point instead made a scoped
  // overlay resolve against the TRIGGER on the mode pages -- the mount is a
  // full-size layer there and the mount point was a content-sized flex item in
  // its corner -- so "fullscreen" measured 20x48 and its input landed under the
  // site nav. Caught by the capture run's geometry specs (item #8778); it is
  // also what the demo bootstrap did before the mount paths merged.
  //
  // A position the page set itself is left alone: the host is the page's
  // element, and it may already be a containing block for reasons of its own.
  if (overlayScope === 'self') {
    if (!hostEl.style.position) {
      hostEl.style.position = 'relative';
    }
    // A containing block is not a stacking context, and the overlay needs both.
    // `position: relative` with `z-index: auto` creates no context, so the
    // overlay's `z-index: 9999` was never resolved against the placement at all
    // -- it participated in the ROOT context and outranked the page's own
    // chrome. Measured taking hit-tests off a `z-index: 1070` sticky header
    // through a frame it was correctly contained inside (item #8798, decision
    // #5092). Scoped means scoped on both axes (decisions #5067, #5084).
    //
    // `isolation` rather than `z-index: 0`: it does not depend on the element
    // being positioned, so it still holds in the branch above that leaves a
    // page-set inline position alone.
    hostEl.style.isolation = 'isolate';
  }
  // A host may make its own box click-through -- the mode pages lay a full-size
  // mount over the simulated site so a scoped overlay has a box to fill, and
  // the site behind it has to stay reachable. `pointer-events` inherits across
  // the shadow boundary, so without this the whole widget goes click-through
  // with it. The page used to re-enable its light-DOM children with a `> *`
  // rule, which cannot reach in here (item #8776).
  mountPoint.style.pointerEvents = 'auto';
  if (mountPoint.parentNode !== shadow) shadow.appendChild(mountPoint);
  const root = createRoot(mountPoint);
  root.render(render({ portalTarget: overlayScope === 'self' ? mountPoint : null }));
  return { shadow, mountPoint, root };
}
